import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import {
  ItemsToWorkspacesData,
  TagsToItemsData,
  AddTagToWorkspaceData,
  DeleteItemsFromWorkspaceData,
  Workspace,
  WorkspaceMember
} from '../DataTypes';
import { AccountService } from './account.service';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceService {
  private http = inject(HttpClient);
  private accountService = inject(AccountService);

  getAllWorkspaces(): Observable<any> {
    const url = environment.apiURL + '/workspaces';
    return this.http.get<Workspace[]>(url);
  }

  createNewWorkspace(formData: any): Observable<any> {
    const url = environment.apiURL + '/workspaces';
    const payload = {
      description: formData.description,
      name: formData.workspaceName,
      userOids: formData.userOidList
    };
    return this.http.post(url, payload);
  }

  addItemsToWorkspaces(addItemsToWorkspaceData: ItemsToWorkspacesData): Observable<any> {
    const url = environment.apiURL + '/workspaces/item-links';
    return this.http.put(url, addItemsToWorkspaceData);
  }

  removeItemsFromWorkspaces(removeItemsToWorkspaceData: ItemsToWorkspacesData): Observable<any> {
    const url = environment.apiURL + '/workspaces/item-links';
    return this.http.delete(url, { body: removeItemsToWorkspaceData });
  }

  restoreItems(removeItemsToWorkspaceData: ItemsToWorkspacesData): Observable<any> {
    const url = environment.apiURL + '/workspaces/items';
    return this.http.patch(url, removeItemsToWorkspaceData);
  }

  deleteItems(deleteItemsData: ItemsToWorkspacesData): Observable<any> {
    const url = environment.apiURL + '/workspaces/items';
    return this.http.delete(url, { body: deleteItemsData });
  }

  addTagsToItems(addTagsToItemsData: TagsToItemsData): Observable<any> {
    const url = environment.apiURL + '/tags/item-tag-links';
    return this.http.put(url, addTagsToItemsData);
  }

  removeTagsFromItems(removeTagsFromItemsData: TagsToItemsData): Observable<any> {
    const url = environment.apiURL + '/tags/item-tag-links';
    return this.http.delete(url, { body: removeTagsFromItemsData });
  }

  deleteTagsFromWorkspace(
    deleteTagsFromWorkspaceData: DeleteItemsFromWorkspaceData
  ): Observable<any> {
    const url =
      environment.apiURL + '/workspaces/' + deleteTagsFromWorkspaceData.workspaceId + '/tags';
    const payload = {
      tagIds: deleteTagsFromWorkspaceData.itemIds
    };
    return this.http.delete(url, { body: payload });
  }

  addTagToWorkspace(addTagToWorkspaceData: AddTagToWorkspaceData): Observable<any> {
    const url = environment.apiURL + '/workspaces/' + addTagToWorkspaceData.workspaceId + '/tags';
    const payload = {
      tagName: addTagToWorkspaceData.tagName
    };
    return this.http.post(url, payload);
  }

  leaveWorkspace(workspaceId: number, newCreatorGid?: string): Observable<any> {
    const url = environment.apiURL + '/workspaces/' + workspaceId + '/members/me';
    return this.http.delete(url, {
      params: newCreatorGid ? { newCreatorGid: newCreatorGid } : {}
    });
  }

  deleteWorkspace(workspaceId: number): Observable<any> {
    const url = environment.apiURL + '/workspaces/' + workspaceId;
    return this.http.delete(url);
  }

  downloadManual() {
    // Set responseType to 'blob' for binary files
    const url = environment.apiURL + '/manuals/user-guide.pptx';
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  getWorkspaceMembers(workspaceId: number): Observable<WorkspaceMember[]> {
    const url = environment.apiURL + '/workspaces/' + workspaceId + '/members';
    return this.http.get<WorkspaceMember[]>(url);
  }

  addUserToWorkspace(userOids: string[], workspaceId: number): Observable<any> {
    const url = environment.apiURL + '/workspaces/' + workspaceId + '/members';
    const payload = {
      userOids: userOids
    };
    return this.http.post(url, payload);
  }

  removeUserFromWorkspace(workspaceId: number, userOid: string): Observable<any> {
    const url = environment.apiURL + '/workspaces/' + workspaceId + '/members/' + userOid;
    return this.http.delete(url);
  }

  searchUsers(query: string): Observable<any> {
    const url = environment.apiURL + '/users';
    return this.http
      .get<{ oid: string; firstName: string; lastName: string }>(url, { params: { query: query } })
      .pipe(
        map((response: any) => {
          const oid = this.accountService.accountSummary()!.oid;
          // Filter out entry with own OID
          return response.filter((user: any) => user.oid !== oid);
        })
      );
  }
}
